/**
 * NKBA API
 * The RESTful backend behind the NKBA application
 *
 * OpenAPI spec version: V1.0.0
 * Contact: cloud@nkba.org
 *
 * NOTE: This class is auto generated by the swagger code generator program.
 * https://github.com/swagger-api/swagger-codegen.git
 * Do not edit the class manually.
 */

import { AxiosRequestConfig } from 'axios';
import { axios } from '../axios.config';

import { EmailRequest } from '../models/EmailRequest';
import { Order } from '../models/Order';
import { OrderRequest } from '../models/OrderRequest';
import { RefundRequest } from '../models/RefundRequest';
import { ResultsPartialOrderInstant } from '../models/ResultsPartialOrderInstant';

/* tslint:disable:no-unused-variable member-ordering max-line-length */

export class OrderApiResource {

    /**
     * Cancel an order
     * 
     * @param orderId Order id to cancel
     */
    public cancelOrder(orderId: Partial<string>, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/{orderId}/cancel'
            .replace('{' + 'orderId}', String(orderId));
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Place an order
     * 
     * @param body Order data to create
     */
    public createOrder(body?: Partial<OrderRequest>, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order';
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Export orders CSV file
     * 
     * @param start Start timestamp
     * @param end End timestamp
     */
    public exportCsv(query?: { start?: string, end?: string }, axiosConfig?: AxiosRequestConfig): Promise<{}> {
        const reqPath = '/v1/order/export';
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return an order of a number of additional locations, prorated to the next renewal date
     * 
     * @param memberId Member id
     * @param qty Quantity of additional locations to buy
     */
    public getAdditionalLocationsOrder(memberId: Partial<string>, query?: { qty?: number }, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/locations/{memberId}'
            .replace('{' + 'memberId}', String(memberId));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return a dues renewal order for a particular member
     * 
     * @param memberId Member id
     */
    public getDuesRenewalOrder(memberId: Partial<string>, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/dues/{memberId}'
            .replace('{' + 'memberId}', String(memberId));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return a particular order by id
     * 
     * @param id Order id
     */
    public getOrder(id: Partial<string>, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/{id}'
            .replace('{' + 'id}', String(id));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return a particular order by number
     * 
     * @param num Order number
     */
    public getOrderByNumber(num: Partial<number>, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/number/{num}'
            .replace('{' + 'num}', String(num));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return the PDF version of a particular order
     * 
     * @param id Order id
     * @param download Download file
     */
    public getOrderPdf(id: Partial<string>, query?: { download?: boolean }, axiosConfig?: AxiosRequestConfig): Promise<{}> {
        const reqPath = '/v1/order/{id}/pdf'
            .replace('{' + 'id}', String(id));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return previous orders, paginated
     * 
     * @param memberId member id to search
     * @param status status to search
     * @param from date to search back from
     * @param limit number of results to return
     */
    public getOrders(query?: { memberId?: string, status?: string, from?: number, limit?: number }, axiosConfig?: AxiosRequestConfig): Promise<ResultsPartialOrderInstant> {
        const reqPath = '/v1/order';
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return woocommerce orders
     * 
     * @param email member email to search
     */
    public getWoocommerceOrders(query?: { email?: string }, axiosConfig?: AxiosRequestConfig): Promise<any[]> {
        const reqPath = '/v1/order/woocommerce';
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return a potentially prorated product order for a particular member
     * 
     * @param memberId Member id
     * @param sku Product sku
     */
    public getProratedOrder(memberId: Partial<string>, query?: { sku?: string }, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/prorated/{memberId}'
            .replace('{' + 'memberId}', String(memberId));
        let reqConfig = {
            ...axiosConfig,
            method: 'GET',
            url: reqPath,
            params: query
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Return a product order for a particular member
     *
     * @param memberId Member id
     * @param sku Product sku
     */
    public getNewOrder(memberId: Partial<string>, query?: { sku?: string }, axiosConfig?: AxiosRequestConfig): Promise<Order> {
      const reqPath = '/v1/order/neworder/{memberId}'
        .replace('{' + 'memberId}', String(memberId));
      let reqConfig = {
        ...axiosConfig,
        method: 'GET',
        url: reqPath,
        params: query
      };
      return axios.request(reqConfig)
        .then(res => {
          return res.data;
        });
    }

    /**
     * Refund all or part of an order
     * 
     * @param orderId Order id to refund
     * @param body Refund information
     */
    public refundOrder(orderId: Partial<string>, body?: Partial<RefundRequest>, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/{orderId}/refund'
            .replace('{' + 'orderId}', String(orderId));
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Resend the email for a particular order
     * 
     * @param id Order id
     * @param body Email to send to (instead of account email)
     */
    public sendOrderEmail(id: Partial<string>, body?: Partial<EmailRequest>, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/{id}/email'
            .replace('{' + 'id}', String(id));
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Send a snailmail letter to a member who placed an order
     * 
     * @param id Order id
     */
    public sendOrderInvoiceLetter(id: Partial<string>, axiosConfig?: AxiosRequestConfig): Promise<{}> {
        const reqPath = '/v1/order/{id}/letter'
            .replace('{' + 'id}', String(id));
        let reqConfig = {
            ...axiosConfig,
            method: 'POST',
            url: reqPath
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

    /**
     * Make changes to an order (currently only shippingAddress)
     * 
     * @param orderId Order id to update
     * @param body Data to update
     */
    public updateOrder(orderId: Partial<string>, body?: Partial<Order>, axiosConfig?: AxiosRequestConfig): Promise<Order> {
        const reqPath = '/v1/order/{orderId}'
            .replace('{' + 'orderId}', String(orderId));
        let reqConfig = {
            ...axiosConfig,
            method: 'PATCH',
            url: reqPath,
            data: body
        };
        return axios.request(reqConfig)
            .then(res => {
                return res.data;
            });
    }

}

export const OrderApi = new OrderApiResource();



// WEBPACK FOOTER //
// ./src/common/api/resources/OrderApi.ts